// frontend_MainDashboard/src/contexts/ClientContext.tsx
import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../constants';

// Types for accessible clients
export interface AccessibleClient {
  id: string;
  name: string;
  phoneNumber: string;
  role: 'owner' | 'admin' | 'agent';
  profilePictureUrl?: string;
}

// Types for user permissions
export interface UserPermissions {
  canInvite: string[];
  canRemove: string[];
  canEditSettings: boolean;
  canDeleteWorkspace: boolean;
  canViewTeamAccess: boolean;
}

// Types for access users (team members with Firebase auth)
export interface AccessUser {
  uid: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'agent';
  addedAt: string | null;
}

// Types for pending invitations
export interface PendingInvitation {
  id: string;
  email: string;
  role: 'admin' | 'agent';
  inviterEmail: string;
  createdAt: string | null;
  expiresAt: string | null;
}

interface ClientContextType {
  // State
  selectedClientId: string | null;
  accessibleClients: AccessibleClient[];
  currentUserRole: 'owner' | 'admin' | 'agent' | null;
  userPermissions: UserPermissions | null;
  isLoading: boolean;
  isLoadingRole: boolean;
  error: string | null;
  
  // Actions
  selectClient: (clientId: string) => void;
  refreshAccessibleClients: () => Promise<void>;
  refreshCurrentUserRole: () => Promise<void>;
  
  // Team access management
  fetchAccessUsers: () => Promise<AccessUser[]>;
  inviteUser: (email: string, role: 'admin' | 'agent') => Promise<{ success: boolean; message: string; invitedUser?: AccessUser; invitationType?: string }>;
  updateUserRole: (uid: string, newRole: 'admin' | 'agent') => Promise<{ success: boolean; message: string }>;
  removeUserAccess: (uid: string) => Promise<{ success: boolean; message: string }>;
  
  // Pending invitations
  fetchPendingInvitations: () => Promise<PendingInvitation[]>;
  cancelPendingInvitation: (invitationId: string) => Promise<{ success: boolean; message: string }>;
  acceptInvitation: (token: string) => Promise<{ success: boolean; message: string }>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Local storage key for selected client
const SELECTED_CLIENT_KEY = 'openMirror_selectedClientId';

export const ClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken, currentUser } = useAuth();
  
  const [selectedClientId, setSelectedClientId] = useState<string | null>(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SELECTED_CLIENT_KEY);
    }
    return null;
  });
  
  const [accessibleClients, setAccessibleClients] = useState<AccessibleClient[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<'owner' | 'admin' | 'agent' | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch accessible clients
  const refreshAccessibleClients = useCallback(async () => {
    if (!idToken) {
      setAccessibleClients([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/user/accessible_clients`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAccessibleClients(data.clients || []);
        
        // Auto-select first client if none selected and clients exist
        if (!selectedClientId && data.clients?.length > 0) {
          const firstClient = data.clients[0];
          setSelectedClientId(firstClient.id);
          localStorage.setItem(SELECTED_CLIENT_KEY, firstClient.id);
        }
        
        // Validate current selection exists in accessible clients
        if (selectedClientId && data.clients) {
          const isValidSelection = data.clients.some((c: AccessibleClient) => c.id === selectedClientId);
          if (!isValidSelection && data.clients.length > 0) {
            // Current selection is no longer valid, switch to first available
            const firstClient = data.clients[0];
            setSelectedClientId(firstClient.id);
            localStorage.setItem(SELECTED_CLIENT_KEY, firstClient.id);
          }
        }
      } else {
        setError(data.message || 'Failed to fetch accessible clients');
      }
    } catch (err) {
      console.error('ClientContext: Error fetching accessible clients:', err);
      setError('Network error while fetching clients');
    } finally {
      setIsLoading(false);
    }
  }, [idToken, selectedClientId]);

  // Fetch current user role for selected client
  const refreshCurrentUserRole = useCallback(async () => {
    if (!idToken || !selectedClientId) {
      setCurrentUserRole(null);
      setUserPermissions(null);
      return;
    }

    setIsLoadingRole(true);
    try {
      const response = await fetch(`${API_BASE_URL}/client/current_user_role`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCurrentUserRole(data.role);
        setUserPermissions(data.permissions);
      } else {
        console.error('ClientContext: Failed to fetch user role:', data.message);
      }
    } catch (err) {
      console.error('ClientContext: Error fetching user role:', err);
    } finally {
      setIsLoadingRole(false);
    }
  }, [idToken, selectedClientId]);

  // Select a client
  const selectClient = useCallback((clientId: string) => {
    setSelectedClientId(clientId);
    localStorage.setItem(SELECTED_CLIENT_KEY, clientId);
  }, []);

  // Fetch access users (team members with access)
  const fetchAccessUsers = useCallback(async (): Promise<AccessUser[]> => {
    if (!idToken || !selectedClientId) {
      return [];
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return data.accessUsers || [];
      } else {
        throw new Error(data.message || 'Failed to fetch access users');
      }
    } catch (err) {
      console.error('ClientContext: Error fetching access users:', err);
      throw err;
    }
  }, [idToken, selectedClientId]);

  // Invite a user
  const inviteUser = useCallback(async (email: string, role: 'admin' | 'agent') => {
    if (!idToken || !selectedClientId) {
      return { success: false, message: 'Not authenticated or no client selected' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.message || (data.success ? 'User invited successfully' : 'Failed to invite user'),
        invitedUser: data.invitedUser,
      };
    } catch (err) {
      console.error('ClientContext: Error inviting user:', err);
      return { success: false, message: 'Network error while inviting user' };
    }
  }, [idToken, selectedClientId]);

  // Update user role
  const updateUserRole = useCallback(async (uid: string, newRole: 'admin' | 'agent') => {
    if (!idToken || !selectedClientId) {
      return { success: false, message: 'Not authenticated or no client selected' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/users/${uid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.message || (data.success ? 'User role updated' : 'Failed to update role'),
      };
    } catch (err) {
      console.error('ClientContext: Error updating user role:', err);
      return { success: false, message: 'Network error while updating role' };
    }
  }, [idToken, selectedClientId]);

  // Remove user access
  const removeUserAccess = useCallback(async (uid: string) => {
    if (!idToken || !selectedClientId) {
      return { success: false, message: 'Not authenticated or no client selected' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/users/${uid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.message || (data.success ? 'User access removed' : 'Failed to remove access'),
      };
    } catch (err) {
      console.error('ClientContext: Error removing user access:', err);
      return { success: false, message: 'Network error while removing access' };
    }
  }, [idToken, selectedClientId]);

  // Fetch pending invitations
  const fetchPendingInvitations = useCallback(async (): Promise<PendingInvitation[]> => {
    if (!idToken || !selectedClientId) {
      return [];
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/pending`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return data.pendingInvitations || [];
      } else {
        throw new Error(data.message || 'Failed to fetch pending invitations');
      }
    } catch (err) {
      console.error('ClientContext: Error fetching pending invitations:', err);
      throw err;
    }
  }, [idToken, selectedClientId]);

  // Cancel pending invitation
  const cancelPendingInvitation = useCallback(async (invitationId: string) => {
    if (!idToken || !selectedClientId) {
      return { success: false, message: 'Not authenticated or no client selected' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/client/access/pending/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'X-Client-ID': selectedClientId,
        },
      });

      const data = await response.json();

      return {
        success: data.success,
        message: data.message || (data.success ? 'Invitation cancelled' : 'Failed to cancel invitation'),
      };
    } catch (err) {
      console.error('ClientContext: Error cancelling invitation:', err);
      return { success: false, message: 'Network error while cancelling invitation' };
    }
  }, [idToken, selectedClientId]);

  // Accept invitation
  const acceptInvitation = useCallback(async (token: string) => {
    if (!idToken) {
      return { success: false, message: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/invitation/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inviteToken: token }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh clients list to show the new workspace
        await refreshAccessibleClients();
        return { success: true, message: 'Invitation accepted successfully' };
      } else {
        return { success: false, message: data.message || 'Failed to accept invitation' };
      }
    } catch (err) {
      console.error('ClientContext: Error accepting invitation:', err);
      return { success: false, message: 'Network error while accepting invitation' };
    }
  }, [idToken, refreshAccessibleClients]);

  // Fetch accessible clients when user logs in
  useEffect(() => {
    if (currentUser && idToken) {
      refreshAccessibleClients();
    } else {
      setAccessibleClients([]);
      setSelectedClientId(null);
      setCurrentUserRole(null);
      setUserPermissions(null);
    }
  }, [currentUser, idToken, refreshAccessibleClients]);

  // Fetch user role when selected client changes
  useEffect(() => {
    if (selectedClientId) {
      refreshCurrentUserRole();
    }
  }, [selectedClientId, refreshCurrentUserRole]);

  return (
    <ClientContext.Provider
      value={{
        selectedClientId,
        accessibleClients,
        currentUserRole,
        userPermissions,
        isLoading,
        isLoadingRole,
        error,
        selectClient,
        refreshAccessibleClients,
        refreshCurrentUserRole,
        fetchAccessUsers,
        inviteUser,
        updateUserRole,
        removeUserAccess,
        fetchPendingInvitations,
        cancelPendingInvitation,
        acceptInvitation,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

// Custom hook to use the ClientContext
export const useClient = (): ClientContextType => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
